
import React from 'react';
import { Container } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import './Business.css';

export const Business = () => {
  const pageUrl = 'https://rishuy-asakim.co.il/business';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'תהליך רישוי עסקים – מחלקת הנדסה וביקורת עסקים',
    description:
      'מדריך לתהליך רישוי עסקים בישראל: בדיקות מחלקת הנדסה, אישורי משרד הבריאות, הגשת בקשה לרישיון עסק וביקורת עסקים ברשות המקומית.',
    url: pageUrl,
    inLanguage: 'he-IL',
    isPartOf: {
      '@type': 'WebSite',
      name: 'רישוי עסקים ישראל',
      url: 'https://rishuy-asakim.co.il/'
    },
    publisher: {
      '@type': 'Organization',
      name: 'רישוי עסקים ישראל',
      url: 'https://rishuy-asakim.co.il/'
    },
    about: [
      {
        '@type': 'Thing',
        name: 'רישוי עסקים'
      },
      {
        '@type': 'Thing',
        name: 'הוצאת רישיון עסק'
      },
      {
        '@type': 'Thing',
        name: 'תכנון הנדסי לעסקים'
      },
      {
        '@type': 'Thing',
        name: 'ביקורת עסקים'
      }
    ]
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ראשי',
        item: 'https://rishuy-asakim.co.il/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'תהליך הרישוי וביקורת עסקים',
        item: pageUrl
      }
    ]
  };

  return (
    <Container fluid className="p-0">

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="business-page">

        <div className="business-wrapper">

          <div className="business-header">

            <div className="business-decorator"></div>

            <h1 className="business-main-title">
              תהליך הרישוי, מבנה מחלקת הנדסה וביקורת עסקים
            </h1>

            <p className="business-intro">
              הדרך לרישיון העסק עוברת דרך מספר תחנות מרכזיות:
              משרד הבריאות, מחלקת ההנדסה על שתי יחידותיה,
              ומחלקת ביקורת עסקים.
            </p>

            <p className="business-intro">
              הבנת תפקידה של כל יחידה ומחלקה תסייע לכם לעבור
              את התהליך בצורה חלקה, לחסוך זמן יקר ולמנוע
              טעויות תכנוניות חמורות.
            </p>

            <div className="business-highlight">
              <strong>
                להלן פירוט מבנה תהליך הרישוי והגורמים המעורבים בו:
              </strong>
            </div>

          </div>

          <div className="business-stage">

            <div className="business-stage-header">

              <div className="business-stage-number">01</div>

              <div>
                <h2 className="business-stage-title">
                  בדיקת תקינות וטיפול מול מחלקת הנדסה לעסקים
                </h2>

                <p className="business-stage-subtitle">
                  בדיקת הנכס הפיזי והתאמתו לחוק
                </p>
              </div>

            </div>

            <p className="business-text">
              מחלקת הנדסה לעסקים היא הגוף האמון על בדיקת
              הנכס הפיזי והתאמתו לחוק. כדי לייעל את העבודה,
              המחלקה עשויה להיות מחולקת ליחידות שונות,
              כאשר כל אחת בוחנת היבט אחר של העסק.
            </p>

            <div className="business-inner-card">

              <div className="business-inner-number">א</div>

              <div>

                <h3 className="business-inner-title">
                  היחידה לתכנון הנדסי
                </h3>

                <p className="business-text">
                  יחידה זו מתמקדת בתכנון הפנימי של העסק
                  ובפונקציונליות שלו. היא מטפלת בתוכניות
                  המפורטות לניהול העסק שהוגשו על ידי בעל
                  מקצוע מוסמך, כגון אדריכל או מהנדס,
                  בהתאם לדרישות הדין.
                </p>

                <div className="business-question-box">

                  <strong>מה היחידה בודקת?</strong>

                  <ul className="business-list">
                    <li>חלוקת המדורים הפנימית</li>
                    <li>עמידה בשטחי המינימום הנדרשים לסוג העסק</li>
                    <li>פתרונות נגישות לאנשים עם מוגבלויות</li>
                    <li>התאמת התכנון לדרישות החוק</li>
                    <li>תוכנית להצבת פרגוד או סגירת חורף במידת הצורך</li>
                  </ul>

                </div>

              </div>

            </div>

            <div className="business-inner-card">

              <div className="business-inner-number">ב</div>

              <div>

                <h3 className="business-inner-title">
                  היחידה לרישוי הנדסי
                </h3>

                <p className="business-text">
                  יחידה זו מתמקדת בחוקיות המבנה והקרקע
                  ומחברת בין חוק רישוי עסקים לבין חוק
                  התכנון והבנייה.
                </p>

                <div className="business-question-box">

                  <strong>מה היחידה בודקת?</strong>

                  <ul className="business-list">
                    <li>האם למבנה קיים היתר בנייה חוקי</li>
                    <li>האם אופי העסק תואם את ייעוד הקרקע</li>
                    <li>התאמה לתוכנית בניין עיר (תב"ע)</li>
                  </ul>

                </div>

                <div className="business-warning">

                  <strong>טיפול בחריגות – שימוש חורג:</strong>

                  <p className="business-small-text">
                    במידה שהשימוש המבוקש לעסק אינו תואם
                    להיתר הבנייה או לתוכנית החלה על הקרקע,
                    ייתכן שיידרש הליך תכנוני מתאים, כגון
                    בקשה לשימוש חורג, בהתאם לנסיבות ולדין.
                  </p>

                </div>

              </div>

            </div>

          </div>

          <div className="business-stage">

            <div className="business-stage-header">

              <div className="business-stage-number">02</div>

              <div>

                <h2 className="business-stage-title">
                  חוות דעת משרד הבריאות לתכנון העסק
                </h2>

                <p className="business-stage-subtitle">
                  בדיקת תברואה, סניטציה והתאמת התכנון
                </p>

              </div>

            </div>

            <p className="business-text">
              עבור עסקים מסוימים, בדגש על תחומי המזון,
              הקוסמטיקה והבריאות, עשויה להידרש חוות דעת
              או עמידה בדרישות משרד הבריאות כחלק מתהליך
              הרישוי, בהתאם לסוג העסק ולפריט הרישוי.
            </p>

            <div className="business-info-card">

              <div className="business-info-icon">✓</div>

              <div>

                <h3 className="business-info-title">
                  אישור תוכניות מוקדם
                </h3>

                <p className="business-text">
                  בשלב זה עשויות להיות מוגשות תוכניות
                  אדריכליות מפורטות, בהתאם לדרישות הגורם
                  המאשר ולמאפייני העסק, לצורך בחינת התכנון
                  וקבלת חוות דעת.
                </p>

              </div>

            </div>

            <div className="business-info-card">

              <div className="business-info-icon">✓</div>

              <div>

                <h3 className="business-info-title">
                  תברואה וסניטציה
                </h3>

                <p className="business-text">
                  במסגרת הבדיקה נבחנים, בהתאם לסוג העסק,
                  מניעת זיהום צולב, הפרדה בין חומרי גלם
                  למוצרים מוכנים, מערכות נידוף וסינון אוויר,
                  מיקומי כיורים ומדורים, פתרונות להפרדת
                  שומנים ופינוי אשפה.
                </p>

              </div>

            </div>

          </div>

          <div className="business-stage">

            <div className="business-stage-header">

              <div className="business-stage-number">03</div>

              <div>

                <h2 className="business-stage-title">
                  הגשת הבקשה הרשמית לרישיון העסק
                </h2>

                <p className="business-stage-subtitle">
                  הגשת תיק הבקשה למחלקת רישוי עסקים
                </p>

              </div>

            </div>

            <p className="business-text">
              לאחר הכנת התוכניות והמסמכים הנדרשים,
              ניתן להגיש בקשה לרישיון עסק לרשות המקומית.
              הגורמים המאשרים והמסמכים הדרושים נקבעים
              בהתאם לסוג העסק ולפריט הרישוי.
            </p>

            <div className="business-checklist">

              <h3 className="business-checklist-title">
                מה עשוי לכלול תיק הבקשה?
              </h3>

              <div className="business-check-item">
                <span className="business-check-icon">✓</span>
                <span>תוכניות חתומות</span>
              </div>

              <div className="business-check-item">
                <span className="business-check-icon">✓</span>
                <span>חוזים ומסמכים נדרשים</span>
              </div>

              <div className="business-check-item">
                <span className="business-check-icon">✓</span>
                <span>אישורי תשלום אגרות</span>
              </div>

              <div className="business-check-item">
                <span className="business-check-icon">✓</span>
                <span>חוות דעת ואישורים מגורמי הרישוי, ככל שנדרשים</span>
              </div>

            </div>

            <div className="business-success-box">

              <div className="business-success-icon">✓</div>

              <div>

                <h3 className="business-success-title">
                  היתר זמני
                </h3>

                <p className="business-text">
                  במקרים המתאימים, ובהתאם להוראות הדין
                  ולהחלטת רשות הרישוי, ניתן לקבל היתר זמני
                  להפעלת העסק לתקופה מוגבלת, בכפוף לתנאים
                  שנקבעו ולהמשך השלמת דרישות הרישוי.
                  הגשת בקשה כשלעצמה אינה מבטיחה קבלת היתר.
                </p>

              </div>

            </div>

          </div>

          <div className="business-stage">

            <div className="business-stage-header">

              <div className="business-stage-number">04</div>

              <div>

                <h2 className="business-stage-title">
                  מחלקת ביקורת עסקים והכנה לסיור בשטח
                </h2>

                <p className="business-stage-subtitle">
                  בדיקה פיזית של העסק והתאמה לתוכניות
                </p>

              </div>

            </div>

            <p className="business-text">
              לאחר הקמת העסק או ביצוע השיפוץ בהתאם לתוכניות,
              עשויה להתבצע ביקורת בשטח מטעם הרשות המקומית
              או גורמי האישור, בהתאם לדרישות הרישוי.
            </p>

            <div className="business-audit-box">

              <div className="business-audit-icon">🔍</div>

              <div>

                <h3 className="business-audit-title">
                  ביקורת התאמה ותברואה
                </h3>

                <p className="business-text">
                  במסגרת הביקורת נבדקת התאמת העסק לתוכניות
                  שאושרו ולדרישות הרלוונטיות, לרבות תנאי
                  תברואה, בטיחות ודרישות מקצועיות נוספות,
                  בהתאם לסוג העסק ולגורם המבקר.
                </p>

              </div>

            </div>

            <div className="business-tip-box">

              <div className="business-tip-icon">💡</div>

              <div>

                <h3 className="business-tip-title">
                  כלים לעזרת בעל העסק
                </h3>

                <p className="business-tip-text">
                  כדי להגיע מוכנים לביקורת ולמנוע עיכובים,
                  מומלץ להיעזר במפרט האחיד ובמסמכי ההנחיות
                  הרשמיים של הרשות המקומית ושל גורמי האישור.
                  הכנת רשימת בדיקה מראש יכולה לסייע באיתור
                  ליקויים ובהשלמת הדרישות לפני הביקורת.
                </p>

              </div>

            </div>

          </div>

          <div className="business-summary">

            <div className="business-summary-icon">✓</div>

            <div>

              <h2 className="business-summary-title">
                חשוב לזכור
              </h2>

              <p className="business-summary-text">
                תהליך רישוי העסק דורש תיאום בין מספר גורמים
                מקצועיים ורשויות. הכנה מוקדמת, בדיקת הנכס
                ותכנון נכון יכולים לחסוך זמן, כסף ועיכובים
                מיותרים בדרך לקבלת רישיון העסק.
              </p>

            </div>

          </div>

        </div>

      </div>

    </Container>
  );
};
